import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const bodyScrollLockOwners = new Set();

/** @type {{ scrollY: number, width: string, position: string, top: string } | null} */
let bodyScrollLockState = null;

/**
 * Keep page scroll locked until every concurrently open dialog has released it.
 * @param {DialogComponent} owner
 * @param {number} scrollY
 */
function acquireBodyScrollLock(owner, scrollY) {
  if (bodyScrollLockOwners.has(owner)) return;

  if (bodyScrollLockOwners.size === 0) {
    bodyScrollLockState = {
      scrollY,
      width: document.body.style.width,
      position: document.body.style.position,
      top: document.body.style.top,
    };
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  bodyScrollLockOwners.add(owner);
}

/**
 * @param {DialogComponent} owner
 * @returns {boolean} Whether this owner held a scroll lock.
 */
function releaseBodyScrollLock(owner) {
  const released = bodyScrollLockOwners.delete(owner);
  if (!released) return false;
  if (bodyScrollLockOwners.size > 0) return true;

  const state = bodyScrollLockState;
  bodyScrollLockState = null;
  if (!state) return true;

  document.body.style.width = state.width;
  document.body.style.position = state.position;
  document.body.style.top = state.top;
  window.scrollTo({ top: state.scrollY, behavior: 'instant' });
  return true;
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    void this.closeDialog();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /** @type {HTMLElement | null} */
  #triggerElement = null;

  #openSequence = 0;

  #pendingOpen = false;

  #isClosing = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#pendingOpen || this.#isClosing) return;

    const scrollY = window.scrollY;
    const openSequence = ++this.#openSequence;
    this.#pendingOpen = true;
    this.#triggerElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (openSequence !== this.#openSequence || !this.isConnected || dialog.open) {
        if (openSequence === this.#openSequence) this.#pendingOpen = false;
        return;
      }

      this.#pendingOpen = false;
      acquireBodyScrollLock(this, scrollY);
      try {
        dialog.showModal();
      } catch (error) {
        releaseBodyScrollLock(this);
        this.#triggerElement = null;
        this.dispatchEvent(new DialogCloseEvent());
        throw error;
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    const hadPendingOpen = this.#pendingOpen;
    this.#pendingOpen = false;
    ++this.#openSequence;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    if (!dialog.open) {
      const releasedScrollLock = releaseBodyScrollLock(this);
      this.#triggerElement = null;
      if (hadPendingOpen || releasedScrollLock) this.dispatchEvent(new DialogCloseEvent());
      return;
    }

    if (this.#isClosing) return;

    this.#isClosing = true;

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    try {
      if (dialog.open) dialog.close();
    } finally {
      dialog.classList.remove('dialog-closing');
      releaseBodyScrollLock(this);
      this.#isClosing = false;
    }

    const triggerElement = this.#triggerElement;
    this.#triggerElement = null;
    if (triggerElement?.isConnected && !triggerElement.hasAttribute('disabled')) {
      triggerElement.focus({ preventScroll: true });
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
